import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, CaretDown } from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { PostCard } from "../components/PostCard";
import { CheckinCard } from "../components/CheckinCard";
import { LockedCard, TrialBadge } from "../components/Paywall";
import { ImageUploader } from "../components/ImageUploader";
import { AnnouncementCard, AnnouncementComposer } from "../components/Announcements";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Feed() {
  const { user, sub } = useAuth();
  const isAdmin = user?.role === "admin";
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("all");
  const [posts, setPosts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [composeChannel, setComposeChannel] = useState("general");
  const [postImages, setPostImages] = useState([]);
  const [posting, setPosting] = useState(false);

  const loadChannels = async () => {
    const res = await api.get("/channels");
    setChannels(res.data);
  };

  const loadAnnouncements = async () => {
    const res = await api.get("/announcements");
    setAnnouncements(res.data);
  };

  const loadPosts = async (ch) => {
    setLoading(true);
    try {
      const res = await api.get("/posts", { params: ch && ch !== "all" ? { channel: ch } : {} });
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChannels(); loadAnnouncements(); }, []);
  useEffect(() => { loadPosts(active); }, [active]);

  const submitPost = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post("/posts", { content, channel: composeChannel, image_paths: isAdmin ? postImages : undefined });
      setContent("");
      setPostImages([]);
      toast.success("Shared anonymously 🌿");
      loadPosts(active);
      loadChannels();
    } catch {
      toast.error("Could not share your post");
    } finally {
      setPosting(false);
    }
  };

  const onDeleted = (id) => {
    setPosts((p) => p.filter((x) => x.id !== id));
    loadChannels();
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#2C362B] tracking-tight">
            Hey, {user?.handle?.split(" ")[1] || "friend"}
          </h1>
          <p className="text-[#6D7A6A] mt-2 text-lg">This is your safe space. What's on your mind today?</p>
        </motion.div>

        <CheckinCard onPickPrompt={(p) => setContent((c) => (c ? c : p + "\n\n"))} />

        <TrialBadge sub={sub} />

        {/* Announcements (admin authored, everyone reads) */}
        <AnnouncementComposer onCreated={(a) => setAnnouncements((prev) => [a, ...prev])} />
        {announcements.length > 0 && (
          <div className="mb-6" data-testid="announcements-list">
            {announcements.map((a) => (
              <AnnouncementCard key={a.id} ann={a} isAdmin={isAdmin} onDeleted={(id) => setAnnouncements((prev) => prev.filter((x) => x.id !== id))} />
            ))}
          </div>
        )}

        {/* Composer (gated by membership) */}
        {sub && !sub.access ? (
          <div className="mb-8">
            <LockedCard subtitle="Reading is always free. Become a member to share your own posts." />
          </div>
        ) : (
        <form onSubmit={submitPost} className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgba(44,54,43,0.03)] border border-[#E6EBE4]/60 mb-8">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Let it out. You're posting anonymously as a random handle…"
            maxLength={5000}
            className="w-full bg-[#F4F1E8] border-none rounded-3xl p-5 text-[#2C362B] text-base placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white resize-none outline-none transition-all duration-300 min-h-[120px]"
            data-testid="compose-textarea"
          />
          {isAdmin && (
            <div className="mt-3">
              <ImageUploader paths={postImages} onChange={setPostImages} testid="post-image-uploader" />
            </div>
          )}
          <div className="flex items-center justify-between mt-4 gap-3">
            <Select value={composeChannel} onValueChange={setComposeChannel}>
              <SelectTrigger className="w-auto rounded-full bg-[#FDFBF7] border-[#EAE6D9] text-[#515A4F] font-semibold" data-testid="compose-channel-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-2xl border-[#EAE6D9]">
                {channels.map((c) => (
                  <SelectItem key={c.slug} value={c.slug} className="rounded-lg">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="submit"
              disabled={posting || !content.trim()}
              className="flex items-center gap-2 bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
              data-testid="compose-submit-btn"
            >
              <PaperPlaneTilt size={18} weight="fill" />
              {posting ? "Sharing…" : "Share"}
            </button>
          </div>
        </form>
        )}

        {/* Channel pills */}
        <div className="flex gap-2.5 overflow-x-auto hide-scrollbar pb-2 mb-6 -mx-4 px-4">
          <Pill label="All" active={active === "all"} onClick={() => setActive("all")} testid="channel-all" />
          {channels.map((c) => (
            <Pill key={c.slug} label={c.name} count={c.post_count} active={active === c.slug} onClick={() => setActive(c.slug)} testid={`channel-${c.slug}`} />
          ))}
        </div>

        {/* Feed */}
        {loading ? (
          <p className="text-center text-[#8A9A86] py-12">Loading quiet thoughts…</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <img src="https://images.unsplash.com/photo-1611255550543-b5ecb01dfddc?crop=entropy&cs=srgb&fm=jpg&q=85&w=400" alt="calm leaves" className="w-40 h-40 object-cover rounded-[2rem] mx-auto mb-6 opacity-90" />
            <p className="font-heading text-xl text-[#5E6C5C]">It's quiet here</p>
            <p className="text-[#8A9A86] mt-1">Be the first to share something in this space.</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="posts-list">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onDeleted={onDeleted} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Pill = ({ label, count, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all whitespace-nowrap border ${
      active
        ? "bg-[#8A9A86] border-[#8A9A86] text-white"
        : "bg-[#FDFBF7] border-[#E6EBE4] text-[#515A4F] hover:border-[#8A9A86] hover:bg-[#E6EBE4]/40"
    }`}
  >
    {label}
    {count > 0 && (
      <span className={`text-xs ${active ? "text-white/80" : "text-[#A8B0A3]"}`}>{count}</span>
    )}
  </button>
);
